import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { PriceMatrix } from 'src/app/modules/pos/quotes/entities/price-matrix.class';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { Customer } from 'src/app/modules/shared/entities/customer.class';
import { ApiService } from 'core/api/api.service';
import { LastQuotesFilters } from '../../entities/last-quotes-filters.class';

@Injectable({
  providedIn: 'root',
})
export class PriceAsService {
  getCustomersAndLeadsUrl = '/api/accountmanager/customersAndLeads/';
  getAmDetailsUrl = '/api/accountmanager/get/';
  getCustomerPriceMatrixUrl = '/api/tools/customerPriceMatrix/';
  getProductPriceUrl = '/api/tools/productPrice';
  getQuotesUrl = '/api/quote/catalog/list';
  getLeadDetailsUrl = '/api/quote/leaddetails/';
  createEditToolUrl = '/api/product/create/edit';
  updateEstimatedShipDate = '/api/product/estimatedShipDate';
  updatePaymentSummary = '/api/payment/summary';
  startNew = '/api/quote/startNew';

  // Data used to populate the quote when starting a new one
  quoteInfo: {
    accountManager?: AccountManager;
    customer?: Customer;
    priceMatrix?: PriceMatrix;
    product: { productNumber: string, quantity: number };
    quoteId?: string;
  } = null;

  constructor(private apiService: ApiService) { }

  getCustomersAndLeads(amEmpId: string): Observable<Customer[]> {
    return this.apiService.get(`${this.getCustomersAndLeadsUrl}${amEmpId}`);
  }

  getAccountManagerDetails(employeeId: string): Observable<AccountManager> {
    return this.apiService.get(`${this.getAmDetailsUrl}${employeeId}`);
  }

  getCustomerPriceMatrix(customerId: string): Observable<PriceMatrix> {
    return this.apiService.get(
      `${this.getCustomerPriceMatrixUrl}${customerId}`
    );
  }

  getLeadDetails(customerId: string): Observable<any> {
    return this.apiService.get(
      `${this.getLeadDetailsUrl}${customerId}`
    );
  }

  getProductPrice(payload: {
    customerId: string;
    priceMatrix: string;
    bpNumber: string;
    quantity: number;
    productNumber: string;
  }): Observable<{ listPrice: number, netPrice: number }> {
    return this.apiService.post(this.getProductPriceUrl, payload);
  }

  clearQuoteData() {
    this.quoteInfo = null;
  }

  /**
   * Gets last quotes, if there are filter values, 10 exact search quotes and 10 partial search quotes are retrieved
   * @param filterValues filters
   * @param amEmployeeId account manager employee id
   * @param customerId customer id
   * @returns an array of QuoteListResponse with exact and partial results or QuoteListResponse no filterValues
   */
  getLastQuotes(
    filterValues: LastQuotesFilters, amEmployeeId: string, customerId?: string
  ) {
    const payloadExactSearch = this.createLastQuotesPayload(filterValues, amEmployeeId, false, customerId);
    const payloadPartialSearch = this.createLastQuotesPayload(filterValues, amEmployeeId, true, customerId);
    if (filterValues) {
      return forkJoin([
        this.apiService.post(this.getQuotesUrl, payloadExactSearch), this.apiService.post(this.getQuotesUrl, payloadPartialSearch)]);
    } else {
      return this.apiService.post(this.getQuotesUrl, payloadExactSearch);
    }
  }

  /**
   * Creates the payload for last ten open quotes
   * @param filterValues filters
   * @param amEmployeeId account manager employee id
   * @param partialSearch partial search flag
   * @param customerId customer id
   * @returns the payload
   */
  createLastQuotesPayload(filterValues: LastQuotesFilters, amEmployeeId: string, partialSearch: boolean, customerId?: string) {
    const payload = {
      employeeId: amEmployeeId,
      customerId,
      date: {
        startDate: filterValues?.startDate || null,
        endDate: filterValues?.endDate || null
      },
      nameFilter: {
        employeeId: amEmployeeId,
        partialSearch
      },
    };

    if (filterValues) {
      payload.nameFilter = {
        employeeId: amEmployeeId,
        bpNumber: filterValues.bpNumber || undefined,
        customerName: filterValues.customerName || undefined,
        quoteNumber: filterValues.quoteNumber || undefined,
        quoteName: filterValues.quoteName || undefined,
        dollarAmount: filterValues.dollarAmount || undefined,
        confidenceToClosePercentageId: filterValues.confidence?.toString() || undefined,
        dollarRangeLow: filterValues.dollarRangeMin || undefined,
        dollarRangeHigh: filterValues.dollarRangeMax || undefined,
        expiringInDays: filterValues.expiringInDays || undefined,
        partialSearch
      } as any;
    }
    return payload;
  }

  /**
   * Add item to order
   * @param productNumber product number
   * @param quantity quantity of the item
   * @param quoteId quote id
   * @returns the payload
   */
  addItemToOrder(
    productNumber: string, quantity: number, quoteId: string
  ) {
    return this.apiService.post(this.createEditToolUrl, { productNumber, quantity, quoteId });
  }

  /**
   * Update item ship date
   * @param id product id
   * @param quoteId quite id
   * @param quantity quantity of the item
   * @returns the payload
   */
  updateShipDate(id: string, quoteId: string, quantity: number) {
    return this.apiService.post(this.updateEstimatedShipDate, { id, quoteId, quantity });
  }

  /**
   * Update quote totals
   * @param id quote id
   * @returns the payload
   */
  calculatePaymentSummary(id: string) {
    return this.apiService.post(this.updatePaymentSummary, { id });
  }

  /**
   * Create new quote for a customer with selected price list and quantity
   * @param customerId selected customer
   * @param createdForEmployeeId selected account manager
   * @param priceList selected price matrix
   * @param productNumber
   * @param quantity
   * @param leadId selected am lead id
   * @returns the payload
   */
  startNewQuote(
    createdForEmployeeId: string, customerId: string, priceList: string, productNumber: string, quantity: number, isLead: boolean
  ) {
    return this.apiService.post(this.startNew, { createdForEmployeeId, customerId, priceList, productNumber, quantity, isLead });
  }
}
